// Boards shared with coworkers, in the browser (Features 1, sharing). A shared board opens with ?shared=<ws>.<board>:
// this page asks for a pass to that board alone (with the person's own sign-in, or a viewer session for someone with
// no board of their own), loads the board as the owner's account gives it, and sends changes with the pass. One page,
// one board: going back to your own boards loads the page again.

import type { Activity, AgentState, Board, CustomTheme, Entry, Item, Stream, Workspace } from "../../shared/types";
import type { SharedBoard, ShareRole } from "../../shared/sharing";
import { api, getToken } from "../lib/api";
import { setMadeThemes } from "../themes/themes";
import { set } from "./store";
import { readStored, removeStored, writeStored } from "../lib/stored";

const VIEWER_KEY = "cb:viewer";

/** Someone signed in only to see what was shared with them: their viewer session. */
export function getViewer(): string | null {
  return readStored(VIEWER_KEY);
}

export function setViewer(t: string | null) {
  // private mode: it lasts as long as the tab
  if (t) writeStored(VIEWER_KEY, t);
  else removeStored(VIEWER_KEY);
}

/** The shared board this page opens, from ?shared=<ws>.<board>. */
export function sharedTarget(): { ws: string; board: string } | null {
  if (typeof location === "undefined") return null;
  const m = /^([a-z0-9]{16})\.([\w-]{1,60})$/.exec(new URLSearchParams(location.search).get("shared") ?? "");
  return m ? { ws: m[1], board: m[2] } : null;
}

/** How a shared board is open here: whose, which, and what this person may do. */
export interface GuestState {
  ws: string;
  board: string;
  role: ShareRole;
  owner: string;
  you: string;
}

/** The pass this page holds; the socket and every change use it. */
let pass: string | null = null;
let passAt = 0;
export const guestPass = () => pass;
/**
 * Where a picture for this board goes, and with what: a board shared with this person takes it through its pass,
 * so the owner's account keeps it (their own would let it go in a week, since none of their posts or pages has it).
 */
export function pictureDoor(path: "post-images" | "pictures" | "pictures/link"): { path: string; token: string | null } {
  return pass ? { path: `/api/guest/${path}`, token: pass } : { path: `/api/${path}`, token: getToken() };
}

/** How long ago this page got its pass: a pass lasts 12 hours. */
export const passAge = () => (pass ? Date.now() - passAt : 0);

interface GuestSnap {
  board: Board;
  theme: CustomTheme | null;
  owner: { name: string; email: string | null };
  you: { email: string; role: ShareRole };
  items: Item[];
  entries: Entry[];
  streams: Stream[];
  agents: AgentState[];
  activity: Activity[];
  serverTime: number;
}

/** A pass to the board: with this person's own board's sign-in, or their viewer session. */
async function askPass(t: { ws: string; board: string }): Promise<{ token: string; role: ShareRole; owner: string }> {
  const session = getToken();
  if (session) return api("/api/shared/pass", { body: t });
  const viewer = getViewer();
  if (viewer) return api("/api/viewer/pass", { body: t, token: viewer });
  throw Object.assign(new Error("Sign in to open the board shared with you."), { code: "signin" });
}

/** The board as the owner's account gives it, into the store the app draws from. */
export function loadGuest(t: { ws: string }, snap: GuestSnap) {
  const items: Record<string, Item> = {};
  for (const i of snap.items) items[i.id] = i;
  const entries: Record<string, Entry> = {};
  for (const e of snap.entries) entries[e.id] = e;
  const streams: Record<string, Stream> = {};
  for (const s of snap.streams) streams[s.key] = s;
  const agents: Record<string, AgentState> = {};
  for (const a of snap.agents) agents[a.name] = a;
  if (snap.theme) setMadeThemes([snap.theme]);
  // the owner's account as far as this page needs one: a name, and nothing of theirs
  const workspace: Workspace = { id: t.ws, name: snap.owner.name, email: null, handle: null, plan: "free", createdAt: 0, lastVisitAt: null, folders: [] };
  set({
    workspace,
    boards: [snap.board],
    archived: [],
    boardId: snap.board.id,
    items,
    entries,
    streams,
    agents,
    activity: snap.activity,
    themes: snap.theme ? { [snap.theme.id]: snap.theme } : {},
    serverOffset: snap.serverTime - Date.now(),
    keys: [],
    guest: { ws: t.ws, board: snap.board.id, role: snap.you.role, owner: snap.owner.email ?? snap.owner.name, you: snap.you.email },
    phase: "ready",
  });
}

/** Opens a shared board on this page: its pass, then the board, then its live link. */
export async function bootShared(t: { ws: string; board: string }) {
  // the owner following their own link: their board, as they always open it
  if (getToken()?.split("_")[1] === t.ws) {
    writeStored("cb:board", t.board); // out of reach: it opens on the board it last showed
    location.replace("/");
    return;
  }
  try {
    const p = await askPass(t);
    pass = p.token;
    passAt = Date.now();
    const snap = await api<GuestSnap>("/api/guest/snapshot", { token: pass });
    loadGuest(t, snap);
    const { connectSocket } = await import("./sync");
    connectSocket();
  } catch (e) {
    const err = e as Error & { code?: string; status?: number };
    if (err.code === "signin" || err.code === "unauthorized") set({ phase: "welcome" });
    else set({ phase: "error", bootError: err.message, bootErrorCode: err.code ?? null });
  }
}

/**
 * A new pass for the board open here, when the one held ran out: the same board, and the role it has now. A role
 * changed meanwhile loads the board again, so the page shows what this person can do. False when it is not shared.
 */
export async function renewPass(): Promise<boolean> {
  const { get } = await import("./store");
  const g = get().guest;
  if (!g) return false;
  try {
    const p = await askPass({ ws: g.ws, board: g.board });
    pass = p.token;
    passAt = Date.now();
    if (p.role !== g.role) await reloadShared();
    return true;
  } catch {
    await bootShared({ ws: g.ws, board: g.board });
    return false;
  }
}

/** Fetched again: a pass that ran out is asked for again, as is one cut back to view only. */
export async function reloadShared() {
  const g = (await import("./store")).get().guest;
  if (!g) return;
  const t = { ws: g.ws, board: g.board };
  try {
    const snap = await api<GuestSnap>("/api/guest/snapshot", { token: pass });
    loadGuest(t, snap);
    const { connectSocket } = await import("./sync");
    connectSocket();
  } catch {
    await bootShared(t);
  }
}

/** What was shared with this person, for their boards list (signed in with a board) or the viewer's home. */
export async function readShared(): Promise<SharedBoard[]> {
  if (getToken()) return (await api<{ shared: SharedBoard[] }>("/api/shared")).shared;
  const v = getViewer();
  return v ? (await api<{ shared: SharedBoard[] }>("/api/viewer/shared", { token: v })).shared : [];
}

/** The address that opens a shared board. */
export const sharedHref = (b: { ws: string; board: string }) => `/?shared=${b.ws}.${encodeURIComponent(b.board)}`;

/** Takes this person off the board open here, and back to their own boards (or what else is shared with them). */
export async function leaveShared(): Promise<void> {
  const { get } = await import("./store");
  const g = get().guest;
  if (!g) return;
  const body = { ws: g.ws, board: g.board };
  if (getToken()) await api("/api/shared/leave", { body });
  else {
    const v = getViewer();
    if (v) await api("/api/viewer/leave", { body, token: v });
  }
  location.href = "/";
}
